import React from 'react';
import styled from 'styled-components';

import Card from 'antd/es/card';
import Divider from 'antd/es/divider';

import doorState from 'config/doorState';
import unloadSpeed from 'config/unloadSpeed';

import { Row, Col, Space } from 'components/layout';
import { Text, Link } from 'components/Text';
import InfoLabel from 'components/InfoLabel';
import ProcessBar from 'components/process/ProcessBar';
import Icon from 'components/Icon';
import ContainerTimeline from 'components/process/ProcessTimeline';
import { formatDuration } from 'helpers/date';

const StyledCard = styled(Card)`
  background: #FFFFFF;

  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  width: 100%;
  height: 100%;
`;

const PeopleCount = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  
  width: 40px;
  height: 20px;
  padding: 4px 8px;
  
  background: #EFF2F5;
  border-radius: 32px;

  &::after {
    content: '${props => props.count || ''}';
    width: 8px;
    height: 12px;
    font-weight: normal;
    font-size: 12px;
    line-height: 100%;
    color: #45494D;
    margin: 0 3px;
  }
`;

const containerTypeToRoute = { 'TRAILER': 'trailers', 'CONTAINER': 'containers' };

const DoorDetailsInfoCard = ({ door, currentContainer, containers, ...rest }) => {
  let {
    container_id,
    container_code,
    relative_speed,
    unloaded_percent,
    unloaded_percent_forecast,
    target_cph = 32,
    expected_people,
    cph,
    unload_method,
    door_state,
    direction,
    default_direction,
    container_type,
    has_container_info,
    hasAnomaly = false,
    customer_name,
    target_time,
    total_time_hrs,
    boxes_unloaded,
    cartons,
    cartons_unloaded,
    cartons_tot,
  } = (door || {});

  const isDoorActive = door_state && door_state !== doorState.EMPTY && door_state !== doorState.NOT_STARTED;
  const hasContainerInfo = has_container_info !== 0;
  const speedColor = isDoorActive && relative_speed ? unloadSpeed.getColor(relative_speed) : null;
  const unloadedPerc = isDoorActive ? Math.floor(unloaded_percent * 100) : 0;
  const unloadedPercForcast = isDoorActive ? Math.floor(unloaded_percent_forecast * 100) : 0;
  const targetCph = isDoorActive && (target_cph !== null && target_cph !== undefined) ? Math.round(target_cph).toString() : null;
  const actualCph = isDoorActive && (cph !== null && cph !== undefined) ? Math.round(cph).toString() : null;
  const containerSpeed = isDoorActive && relative_speed ? relative_speed : null;
  const containerSpeedText = isDoorActive && containerSpeed ? containerSpeed.toLowerCase().replace('_', ' ') : '';
  const unloadMethod = isDoorActive && unload_method ? unload_method : null;
  const containerType = container_type?.toUpperCase();
  direction = direction || default_direction;
  const customerName = customer_name || currentContainer?.customer_name || null;
  const targetTime = isDoorActive
    ? (direction === 'LOAD' ? 'N/A'
      : (typeof target_time === 'number' && target_time !== 0
        ? formatDuration(target_time * 3600000, false)
        : null))
    : null;
  const unloadTime = isDoorActive
    ? (typeof total_time_hrs === 'number'
        ? formatDuration(total_time_hrs * 3600000, false)
        : null)
    : null;
  const cartonsTotal = (cartons_tot !== undefined && cartons_tot !== null)
    ? cartons_tot
    : (cartons !== undefined && cartons !== null)
      ? cartons
      : currentContainer?.cartons;
  const cartonsCount = (typeof cartons_unloaded === 'number')
    ? cartons_unloaded
    : (typeof boxes_unloaded === 'number')
      ? boxes_unloaded
      : undefined;
  const cartonsUnloaded = isDoorActive
    ? (typeof cartonsCount === 'number'
        ? (typeof cartonsTotal === 'number' ? `${Math.round(cartonsCount)}/${cartonsTotal}` : `${Math.round(cartonsCount)}`)
        : null)
    : null;

  return (
    <StyledCard>
      <Row justify="space-between" style={{ padding: '0 0 8px 0' }}>
        <Space>
          <Text>Equipment Code:</Text>
          {door_state !== doorState.EMPTY && container_code ? (
            hasContainerInfo && containerTypeToRoute[containerType] ? (
              <Link to={`/${containerTypeToRoute[containerType]}/${container_id}`}>
                {container_code}
              </Link>
            ) : <Text style={{ color: '#828C94' }}>{container_code}</Text>
          ) : '-'}
        </Space>
        <Space>
          <Text>Customer:</Text>
          {customerName ? (
            <Text style={{ color: '#828C94' }}>{customerName}</Text>
          ) : '-'}
        </Space>
      </Row>
      <Row align="center" style={{ padding: '16px 0' }}>
        <Col span={24}>
          <ContainerTimeline withStripes={hasAnomaly} container={currentContainer} {...rest} />
        </Col>
      </Row>

      <Divider orientation="horizontal" style={{ margin: '16px 0' }} />

      <Row justify="space-between" align="stretch" style={{ marginBottom: '-8px' }}>
        <Col>
          <Space size={64} align="start">
            <div>
            <InfoLabel
                name={`${direction === 'LOAD' ? 'Load' : 'Unload'} Method`}
                value={unloadMethod}
                inline={true}
                style={{
                  fontSize: '12px',
                  lineHeight: '20px'
                }}
              />
              <InfoLabel
                name={direction === 'LOAD' ? 'Cartons Loaded' : 'Cartons Unloaded'}
                value={cartonsUnloaded}
                inline={true}
                style={{
                  fontSize: '12px',
                  lineHeight: '20px'
                }}
              />
            </div>
            <div>
            <InfoLabel
                name="Target Time"
                value={targetTime}
                inline={true}
                style={{
                  fontSize: '12px',
                  lineHeight: '20px'
                }}
              />
              <InfoLabel
                name={`${direction === 'LOAD' ? 'Load' : 'Unload'} Time`}
                value={unloadTime}
                inline={true}
                style={{
                  fontSize: '12px',
                  lineHeight: '20px'
                }}
              />
            </div>
            <div>
              <InfoLabel
                name="Target CPH"
                value={direction === 'LOAD' ? 'N/A' : targetCph}
                inline={true}
                extra={targetCph && expected_people ? (
                  <PeopleCount count={Math.round(expected_people)} style={{ marginLeft: '4px' }}>
                    <Icon name="users" size={12} color={'#B9C2C9'} />
                  </PeopleCount>
                ) : null}
                style={{
                  fontSize: '12px',
                  lineHeight: '20px'
                }}
              />
              <InfoLabel
                name="Actual CPH"
                value={actualCph}
                inline={true}
                style={{
                  fontSize: '12px',
                  lineHeight: '20px'
                }}
              />
            </div>
          </Space>

        </Col>
        <Col>
          <div>
            <InfoLabel
              name="Overall Speed"
              value={containerSpeedText}
              inline={true}
              style={{
                fontSize: '12px',
                lineHeight: '20px',
                textTransform: 'capitalize',
                color: speedColor,
              }}
            />
            <InfoLabel
              name="Current Progress"
              value=" "
              inline={true}
              extra={(
                <ProcessBar
                  completed={unloadedPerc}
                  completedColor={direction === 'LOAD' ? '#232526' : speedColor}
                  forcasted={unloadedPercForcast}
                  forcastedColor={'#828C94'}
                  showPrediction={direction === 'UNLOAD'}
                  style={{ width: '140px' }}
                />
              )}
              style={{
                fontSize: '12px',
                lineHeight: '20px'
              }}
            />
          </div>
        </Col>
      </Row >

    </StyledCard >
  );
};

export default DoorDetailsInfoCard;
